import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
// IMPORTS DIRECTOS a los servicios genéricos de app-common (socket/toast) para evitar el ciclo de DI:
// el barril `@common/services` re-exporta AuthService, que a su vez importa este OnboardingService.
import { CustomResponseInterface } from '@common/interfaces/custom-responce.interface';
import { SocketService } from '@common/services/socket.service';
import { ToastService } from '@common/services/toast.service';
import { UserService } from '@modules/auth/services';
import { environment } from 'environments/environment';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, filter, map, pairwise, take } from 'rxjs/operators';

// Tipos e infra del propio módulo onboarding.
import { OnboardingCelebrationConfig, OnboardingCompletion, OnboardingPhase, OnboardingState, OnboardingTask, OnboardingTheme } from '../interfaces/onboarding.interfaces';
import { defaultPhaseUiConfig, PHASE_UI_CONFIG, TASK_MODULE_MAP } from './onboarding.config';

/**
 * Payload que abre el modal de celebración: los textos vienen del `celebration_config` (config estática)
 * y la métrica (tareas completadas/total) de la fase recién completada del `state$` real.
 */
export interface OnboardingCelebration {
    config: OnboardingCelebrationConfig;
    theme: OnboardingTheme;
    tasksDone: number;
    tasksTotal: number;
}

/** Payload del modal final (FINA-3377): la celebración de la última fase + el bloque `completion` del backend. */
export interface OnboardingCompletionCelebration extends OnboardingCelebration {
    completion: OnboardingCompletion;
}

/**
 * Fuente ÚNICA de la verdad del onboarding en el frontend (fusión de FINA-2529 + FINA-2604).
 *
 * - `loadInitialState()`: una sola petición `GET /api/onboarding/status` al iniciar sesión.
 * - `initializeRealTimeUpdates()`: se conecta al WebSocket de la sala del tenant y parcha el estado al
 *   recibir `onboarding_updated`; ante una reconexión re-pide el estado por HTTP para no perder eventos.
 * - `teardown()`: cierra el socket y limpia el estado en logout.
 * - `state$` (nullable): lo consumen los componentes UI vía `pipe async`.
 * - `activePhase$` / `celebration$`: derivados para el banner del Home y el modal de celebración.
 * - `completion$`: modal final del onboarding (FINA-3377), una sola vez por tenant vía claim atómico.
 *
 * El backend devuelve el contrato base (sin campos de UI); el NORMALIZADOR (`normalizeBackendState`)
 * inyecta layout/theme/cta/celebration_config/etc. desde `onboarding.config.ts`.
 *
 * `loadInitialState`/`initializeRealTimeUpdates` se disparan tras login/auto-login y `teardown` en logout
 * (ver AuthService). `providedIn: 'root'` ⇒ singleton inyectable desde cualquier componente.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService implements OnDestroy {
    private readonly API_URL = environment.API_URL;

    private readonly _state$ = new BehaviorSubject<OnboardingState | null>(null);
    readonly state$: Observable<OnboardingState | null> = this._state$.asObservable();

    /** Valor síncrono del estado (para consumidores que necesitan leerlo sin suscribirse, ej. el orquestador de tours). */
    get currentState(): OnboardingState | null {
        return this._state$.value;
    }

    /**
     * Retención del cierre (FINA-3431). `true` desde que esta sesión inicia el claim del modal final hasta que el
     * modal se cierra (`releaseCompletionHold()`), o hasta que el claim responde `claimed: false`, falla o no
     * puede abrir el modal. Mientras dura, el anillo del navbar (al 100%) y el banner del Home siguen visibles
     * aunque el estado ya esté `completed` (AC-02/AC-05 de FINA-3377). Arranca antes de la respuesta del claim
     * para que no parpadeen entre el estado `completed` y la apertura del modal.
     */
    private readonly _completionHold$ = new BehaviorSubject<boolean>(false);
    readonly completionHold$: Observable<boolean> = this._completionHold$.asObservable();

    /** Lectura síncrona de la retención del cierre (para getters de componentes). */
    get isCompletionHeld(): boolean {
        return this._completionHold$.value;
    }

    private readonly _settled$ = new BehaviorSubject<boolean>(false);
    /**
     * Emite `true` cuando la carga inicial del onboarding YA resolvió: 200 (con o sin estado), 404 ("no aplica")
     * o error de red. Permite a un consumidor (p. ej. el orquestador de tours) DIFERIR una decisión que depende
     * de `currentState` hasta que el HTTP volvió, evitando la race del arranque en frío (F5 / URL directa) donde
     * `currentState` es `null` sólo porque la petición aún no terminó. Se resetea a `false` en `teardown()`
     * (logout) para que el próximo login vuelva a esperar su propia carga. Nunca "carga" indefinidamente: los
     * tres desenlaces del HTTP lo ponen en `true`.
     */
    readonly settled$: Observable<boolean> = this._settled$.asObservable();

    /**
     * Fase activa según la FUENTE DE VERDAD del backend: `currentPhaseId` (y, como respaldo, la fase con
     * `status === 'active'`). El backend calcula el avance de fases (obligatorias completas o vencimiento de
     * `duration_days`); el front NO lo recalcula ni adivina por progreso. Prioridad: `currentPhaseId` →
     * `status === 'active'` → última fase (fallback defensivo si faltara el dato). Null-safe: sin estado
     * (pre-login o post-logout) emite `undefined`. Emite `undefined` también cuando el onboarding está
     * completado (`completion.completed`, FINA-3377) o globalmente al 100% → el banner del Home se oculta,
     * salvo mientras dura la retención del cierre (`completionHold$`, FINA-3431): el banner sigue hasta que se
     * cierra el modal final.
     */
    readonly activePhase$: Observable<OnboardingPhase | undefined> = combineLatest([this.state$, this._completionHold$]).pipe(
        map(([state, held]) => {
            if (!state) return undefined;
            // Retención del cierre (FINA-3431): el banner se retira al cerrar el modal final, no al completarse.
            if (held) return this.getActivePhase(state) ?? undefined;
            // Onboarding cerrado por el backend (FINA-3377): fuente de verdad del fin del onboarding.
            if (state.completion?.completed) return undefined;
            // Onboarding globalmente completo ⇒ no hay fase activa: el banner del Home se oculta (consistente
            // con el chip del navbar, que también se retira al 100%). Sin esta guarda, el fallback "última
            // fase" mostraría indefinidamente una fase ya completada al 100% (el backend no hace 404 al
            // terminar; sigue devolviendo el árbol con todo en 100%).
            if ((state.progress?.overall?.global ?? 0) >= 100) return undefined;
            return state.phases.find(phase => phase.phase_id === state.currentPhaseId) ?? state.phases.find(phase => phase.status === 'active') ?? state.phases[state.phases.length - 1];
        }),
    );

    /**
     * Emite cuando una fase pasa de `overall < 100` a `=== 100` (y trae `celebration_config`).
     * Usa `pairwise()`: la carga inicial produce el par `[null, estado]` (filtrado por el guard de
     * `findJustCompletedPhase`), por lo que recargar con una fase ya completa NO reabre el modal; solo lo
     * hace la transición real. Excluye la ÚLTIMA fase: su celebración es el modal final, que sale por
     * `completion$` (FINA-3377) para mostrarse una sola vez entre sesiones.
     */
    readonly celebration$: Observable<OnboardingCelebration> = this.state$.pipe(
        pairwise(),
        map(([prev, curr]) => this.findJustCompletedPhase(prev, curr)),
        filter(phase => !!phase && !this.isLastPhase(phase, this._state$.value)),
        filter((phase): phase is OnboardingPhase => !!phase?.celebration_config),
        map(phase => ({
            config: phase.celebration_config as OnboardingCelebrationConfig,
            theme: phase.theme ?? 'purple',
            // Métrica desde el estado real de la fase (no desde config.metrics).
            tasksDone: phase.tasks_done,
            tasksTotal: phase.tasks_total,
        })),
    );

    /**
     * Analytics: emite la `OnboardingPhase` que cruzó de `overall < 100` a `=== 100` (mismo detector que
     * `celebration$`, pero expone la fase para instrumentar `onboarding_phase_{n}_completed`).
     */
    readonly phaseCompleted$: Observable<OnboardingPhase> = this.state$.pipe(
        pairwise(),
        map(([prev, curr]) => this.findJustCompletedPhase(prev, curr)),
        filter((phase): phase is OnboardingPhase => !!phase),
    );

    /**
     * Analytics: emite cada tarea que pasó de `isCompleted` false→true entre dos estados, con su fase
     * (para `onboarding_step_completed`). Guarda el par inicial `[null, estado]` en el helper.
     */
    readonly taskCompleted$: Observable<{ task: OnboardingTask; phase: OnboardingPhase }> = this.state$.pipe(
        pairwise(),
        concatMap(([prev, curr]) => this.findJustCompletedTasks(prev, curr)),
    );

    /**
     * Modal final del onboarding (FINA-3377). Cuando el backend marca `completion.showModal`, el servicio
     * hace UN claim atómico (`POST /onboarding/completion-modal/seen`) y emite solo si el backend confirma
     * que esta sesión lo registró (`claimed: true`): así el modal se ve una sola vez aunque haya varias
     * sesiones o recargas. `claimed: false` (otra sesión ganó) no emite; un error de red libera la guardia
     * para reintentar con el próximo estado. Es un Subject alimentado por una suscripción interna para que
     * sus consumidores (shell + analytics) compartan el mismo claim.
     */
    private readonly _completion$ = new Subject<OnboardingCompletionCelebration>();
    readonly completion$: Observable<OnboardingCompletionCelebration> = this._completion$.asObservable();
    private _completionClaimInFlightOrDone = false;
    private completionSub?: Subscription;

    // Suscripciones al socket, para limpiarlas en `teardown()` (logout).
    private socketSubscriptions: Subscription = new Subscription();
    private realTimeInitialized = false;

    /**
     * Suscripción de la carga HTTP inicial (`loadInitialState`). Se cancela antes de re-pedir y en `teardown`
     * (logout) para que una respuesta en vuelo no repueble `state$` DESPUÉS de que el logout hiciera
     * `_state$.next(null)` (race: mostraría el onboarding del usuario que cerró sesión, o pisaría el del nuevo
     * en un re-login rápido).
     */
    private _loadSub: Subscription | null = null;

    /**
     * `true` cuando el onboarding NO aplica a este tenant (el HTTP inicial dio 404: no-Retail o comercio
     * preexistente). Descarta cualquier frame de socket `onboarding_updated` para que un no-Retail LEGACY con
     * doc de progreso (cuyo motor SÍ emite por socket) no reviva el banner/navbar tras el 404. Invariante:
     * `true ⟺ lastRawState === null` (el 404 nunca puebla `lastRawState`) — clave para el reset seguro en `tenant$`.
     */
    private _isOnboardingNotApplicable = false;

    // Último payload crudo del backend. Permite re-normalizar cuando llega el nombre del comercio,
    // que se resuelve async tras el login (después de `loadInitialState()`).
    private lastRawState: any = null;

    // Suscripción a `tenant$` (creada en el constructor, vive toda la vida del servicio singleton).
    // Se limpia en `ngOnDestroy` (destrucción de la app), no en `teardown()` (logout), para no matarla
    // en el primer logout y romper la re-normalización tras logout→login.
    private tenantSub?: Subscription;

    constructor(private http: HttpClient, private socketService: SocketService, private toastService: ToastService, private userService: UserService) {
        // Cuando el tenant (con `companyName`) se resuelve/actualiza tras el login, re-normaliza el último
        // estado para reflejar el nombre del comercio en la tarjeta "Cuenta creada".
        this.tenantSub = this.userService.tenant$.subscribe(() => {
            if (!this.lastRawState) return;
            // Reset DESPUÉS del guard de `lastRawState` (placement crítico): para un no-Retail `lastRawState`
            // es null → early-return arriba → la bandera NUNCA se limpia aquí (sigue descartando socket). Para
            // un Retail (con lastRawState) la bandera ya es false, así que esto es un no-op seguro. Resetearlo
            // ANTES del guard reviviría la UI de un no-Retail cuando `tenant$` re-emite (companyName).
            this._isOnboardingNotApplicable = false;
            // try/catch obligatorio (mismo motivo que en `onboarding_updated`): una excepción no capturada en
            // este `next` completaría la suscripción con error y la re-normalización tras cambios de tenant
            // dejaría de funcionar para el resto de la sesión.
            try {
                this._state$.next(this.normalizeBackendState(this.lastRawState));
            } catch (err) {
                console.error('[OnboardingService] Error re-normalizando el estado tras cambio de tenant:', err);
            }
        });

        // Claim del modal final cuando el backend lo marca como pendiente (ver `completion$`).
        this.completionSub = this.state$
            .pipe(filter(state => !!state?.completion?.showModal && !this._completionClaimInFlightOrDone))
            .subscribe(state => this.claimCompletionModal(state as OnboardingState));
    }

    /** Cleanup de las suscripciones internas al destruir el servicio singleton (destrucción de la app). */
    ngOnDestroy(): void {
        this.tenantSub?.unsubscribe();
        this.completionSub?.unsubscribe();
    }

    /** AC1: carga el estado inicial por HTTP (una sola petición). Normaliza antes de publicar. */
    loadInitialState(): void {
        this._loadSub?.unsubscribe(); // cancela cualquier carga previa en vuelo (evita repoblar tras logout)
        this._loadSub = this.http
            .get<CustomResponseInterface<any>>(`${this.API_URL}/onboarding/status`)
            .pipe(
                map(res => res.data),
                take(1),
            )
            .subscribe({
                next: raw => {
                    if (raw) {
                        this._isOnboardingNotApplicable = false; // 200: el onboarding SÍ aplica (limpia bandera vieja)
                        this.lastRawState = raw;
                        this._state$.next(this.normalizeBackendState(raw));
                    }
                    this._settled$.next(true); // carga resuelta (200): los consumidores diferidos ya pueden decidir
                },
                error: (err: any) => {
                    this._settled$.next(true); // carga resuelta (404/5xx): destraba a los consumidores diferidos
                    // 404 = el tenant no tiene onboarding activo (no-Retail / preexistente): estado esperado, se
                    // silencia. Se marca la bandera para DESCARTAR frames de socket (un no-Retail legacy con doc
                    // igual recibe `onboarding_updated`; sin esto reviviría la UI). Un 5xx NO marca la bandera
                    // (error transitorio ≠ "no aplica"): se avisa con toast.
                    if (err?.status === 404) {
                        this._isOnboardingNotApplicable = true;
                        // Race: el socket pudo emitir `onboarding_updated` y poblar `_state$` ANTES de que llegara
                        // este 404 (un no-Retail legacy con doc de progreso). Limpiamos el estado para que el
                        // banner/navbar no queden visibles en un tenant al que el onboarding NO aplica. La bandera
                        // ya está en `true`, así que los frames de socket posteriores se descartan.
                        this._state$.next(null);
                        return;
                    }
                    this.toastService.error(err?.error?.title || 'Error', err?.error?.message || 'No se pudo cargar el estado del onboarding');
                },
            });
    }

    /**
     * AC2/AC3: conecta el WebSocket y engancha las actualizaciones en tiempo real.
     * Idempotente: si ya se inicializó, no vuelve a suscribir.
     *
     * Nota: el modal de celebración NO se dispara desde aquí. Al hacer `_state$.next(...)` con el estado
     * normalizado, `celebration$` (pairwise) detecta la fase que cruza a 100% y el shell (`AppComponent`)
     * abre el modal. El servicio no referencia la UI (desacople "Cero Cables").
     */
    initializeRealTimeUpdates(): void {
        if (this.realTimeInitialized) return;
        this.realTimeInitialized = true;

        this.socketService.connect();

        // AC2: al recibir el evento se parcha el estado (normalizado) sin recargar la página.
        this.socketSubscriptions.add(
            this.socketService.on<any>('onboarding_updated').subscribe(payload => {
                // El onboarding no aplica a este tenant (404 inicial: no-Retail / preexistente) → descartar el
                // frame para no revivir el banner/navbar (un no-Retail legacy con doc igual recibe emisiones).
                if (this._isOnboardingNotApplicable) return;
                if (!payload) return;
                // try/catch obligatorio: una excepción no capturada en el callback `next` completaría el
                // Observable con error y NINGÚN `onboarding_updated` posterior se procesaría hasta el próximo
                // login (estado congelado sin aviso). Ej.: el backend cambia el contrato y `normalizeBackendState`
                // lanza. Logueamos y seguimos vivos para el siguiente evento.
                try {
                    this.lastRawState = payload;
                    this._state$.next(this.normalizeBackendState(payload));
                } catch (err) {
                    console.error('[OnboardingService] Error normalizando onboarding_updated:', err);
                }
            }),
        );

        // AC3: si la conexión se pierde y reconecta, se re-pide el estado por HTTP.
        this.socketSubscriptions.add(
            this.socketService.reconnect$.subscribe(() => {
                this.loadInitialState();
            }),
        );
    }

    /** Limpia socket, suscripciones y estado. Se llama al cerrar sesión. */
    teardown(): void {
        this._loadSub?.unsubscribe(); // cancela una carga HTTP en vuelo para que no repueble state$ tras logout
        this._loadSub = null;
        this.socketSubscriptions.unsubscribe();
        this.socketSubscriptions = new Subscription();
        this.realTimeInitialized = false;
        this.lastRawState = null;
        this._isOnboardingNotApplicable = false; // pizarra limpia: el próximo login reevalúa aplicabilidad
        this._settled$.next(false); // el próximo login debe volver a esperar SU carga antes de decidir auto-shows
        this._completionClaimInFlightOrDone = false; // el próximo login reevalúa su propio modal final
        this.releaseCompletionHold(); // un logout con el modal final abierto no deja la retención activa
        this.socketService.disconnect();
        this._state$.next(null);
    }

    // ─────────────────────── Normalizador (bridge backend → contrato UI) ───────────────────────
    // El endpoint/socket devuelven el contrato base (progress, tasks, guided_tour). Este método
    // enriquece cada fase/tarea con los campos de UI que el backend aún no envía, tomados de
    // `onboarding.config.ts`. TODO(backend): retirar cuando el backend devuelva estos campos.

    private normalizeBackendState(raw: any): OnboardingState {
        return {
            onboarding_id: raw.onboarding_id,
            version: raw.version,
            description: raw.description,
            // Fuente de verdad de la fase activa (el backend la calcula y la expone).
            currentPhaseId: raw.currentPhaseId ?? '',
            progress: raw.progress,
            phases: (raw.phases ?? []).map((phase: any) => this.normalizePhase(phase)),
            // Cierre del onboarding (FINA-3377), tal cual lo envía el backend. Ausente ⇒ criterio previo.
            completion: raw.completion,
        };
    }

    private normalizePhase(phase: any): OnboardingPhase {
        const ui = PHASE_UI_CONFIG[phase.phase_id] ?? defaultPhaseUiConfig(phase.order);
        const tasks: OnboardingTask[] = (phase.tasks ?? []).map((task: any) => this.normalizeTask(task, ui.theme));
        return {
            phase_id: phase.phase_id,
            title: phase.title,
            order: phase.order,
            timeframe_label: phase.timeframe_label,
            // Estado calculado por el backend ('completed' | 'active' | 'locked'). Se respeta tal cual.
            status: phase.status,
            progress: phase.progress,
            // Campos de UI inyectados desde la config estática:
            layout: ui.layout,
            theme: ui.theme,
            counter_label: ui.counter_label,
            subtitle: ui.subtitle,
            time_left_label: ui.time_left_label,
            // Derivados del propio array de tareas (siempre consistentes con el anillo de progreso):
            tasks_done: tasks.filter(task => task.isCompleted).length,
            tasks_total: tasks.length,
            celebration_config: ui.celebration_config,
            tasks,
        };
    }

    private normalizeTask(task: any, theme: OnboardingTheme): OnboardingTask {
        const normalized: OnboardingTask = {
            ...task,
            // Clave de módulo UI (para el ícono). Si no está mapeada, se conserva la del backend.
            module: TASK_MODULE_MAP[task.task_id] ?? task.module,
            // Fase verde (reportes) usa "Ver"; el resto "Ir". El getter de la card aplica el default igual.
            action_label: task.action_label ?? (theme === 'green' ? 'Ver' : 'Ir'),
            // Campo de UI no provisto por el backend; queda vacío (la card lo omite).
            time_label: task.time_label ?? '',
        };

        // Tarjeta "Cuenta creada": el detalle muestra el nombre del comercio (tenant resuelto en el login).
        if (task.task_id === 'setup_company') {
            normalized.completed_detail = { label: 'Nombre de la empresa', value: this.getCompanyName() };
        }

        return normalized;
    }

    /** Nombre del comercio desde el tenant resuelto en el login (vacío si aún no cargó). */
    private getCompanyName(): string {
        return this.userService.tenant$.value?.companyName ?? '';
    }

    /** Claim atómico del modal final; emite en `completion$` solo si esta sesión lo registró. */
    /** Libera la retención del cierre (FINA-3431). Idempotente: sin retención activa no hace nada. */
    releaseCompletionHold(): void {
        if (this._completionHold$.value) this._completionHold$.next(false);
    }

    private claimCompletionModal(state: OnboardingState): void {
        this._completionClaimInFlightOrDone = true;
        this._completionHold$.next(true);
        this.http
            .post<CustomResponseInterface<{ claimed: boolean }>>(`${this.API_URL}/onboarding/completion-modal/seen`, {})
            .pipe(
                take(1),
                catchError(err => {
                    // Error de red/servidor: no se abre el modal y se libera la guardia para reintentar con el
                    // próximo estado (mejor no mostrarlo que mostrarlo dos veces).
                    console.error('[OnboardingService] Error registrando la vista del modal final:', err);
                    this._completionClaimInFlightOrDone = false;
                    this.releaseCompletionHold();
                    return of(null);
                }),
            )
            .subscribe(res => {
                // Sin modal en esta sesión (otra ganó, error o sin copy final): el anillo y el banner se retiran ya.
                if (res?.data?.claimed !== true || !state.completion) return this.releaseCompletionHold();
                const lastPhase = this.getLastPhase(state);
                if (!lastPhase?.celebration_config) return this.releaseCompletionHold();
                this._completion$.next({
                    config: lastPhase.celebration_config,
                    theme: lastPhase.theme ?? 'purple',
                    tasksDone: lastPhase.tasks_done,
                    tasksTotal: lastPhase.tasks_total,
                    completion: state.completion,
                });
            });
    }

    /** Fase de mayor `order` (la última del onboarding). */
    private getLastPhase(state: OnboardingState | null): OnboardingPhase | undefined {
        return (state?.phases ?? []).reduce<OnboardingPhase | undefined>((last, phase) => (!last || phase.order > last.order ? phase : last), undefined);
    }

    private isLastPhase(phase: OnboardingPhase, state: OnboardingState | null): boolean {
        return this.getLastPhase(state)?.phase_id === phase.phase_id;
    }

    /** Primera fase cuyo `overall` cruzó de <100 a ===100 entre dos estados consecutivos. */
    private findJustCompletedPhase(prev: OnboardingState | null, curr: OnboardingState | null): OnboardingPhase | undefined {
        if (!prev || !curr) return undefined; // ignora el par inicial [null, estado]
        return curr.phases.find(phase => {
            const before = prev.phases.find(p => p.phase_id === phase.phase_id);
            return !!before && before.progress.overall < 100 && phase.progress.overall === 100;
        });
    }

    /** Tareas que pasaron de `isCompleted` false→true entre dos estados (para analytics). */
    private findJustCompletedTasks(prev: OnboardingState | null, curr: OnboardingState | null): Array<{ task: OnboardingTask; phase: OnboardingPhase }> {
        if (!prev || !curr) return []; // ignora el par inicial [null, estado]
        const justCompleted: Array<{ task: OnboardingTask; phase: OnboardingPhase }> = [];
        for (const phase of curr.phases) {
            const before = prev.phases.find(p => p.phase_id === phase.phase_id);
            for (const task of phase.tasks) {
                const beforeTask = before?.tasks.find(t => t.task_id === task.task_id);
                if (beforeTask && !beforeTask.isCompleted && task.isCompleted) {
                    justCompleted.push({ task, phase });
                }
            }
        }
        return justCompleted;
    }

    // ─────────── Getters para los modales (rama FINA-2605-2606) ───────────
    // Alineados a la fuente de verdad del backend (currentPhaseId/status), para NO reintroducir
    // el cálculo por progreso que se retiró en la arquitectura reciente (ver `activePhase$`).

    getActivePhase(onboarding: OnboardingState | null = this._state$.getValue()): OnboardingPhase | null {
        if (!onboarding?.phases?.length) return null;
        return (
            onboarding.phases.find(phase => phase.phase_id === onboarding.currentPhaseId) ??
            onboarding.phases.find(phase => phase.status === 'active') ??
            onboarding.phases[onboarding.phases.length - 1] ??
            null
        );
    }

    getMandatoryTasks(phase: OnboardingPhase | null): OnboardingTask[] {
        return phase?.tasks?.filter(task => task.is_mandatory) || [];
    }

    getOptionalTasks(phase: OnboardingPhase | null): OnboardingTask[] {
        return phase?.tasks?.filter(task => !task.is_mandatory) || [];
    }

    getCompletedCount(tasks: OnboardingTask[] = []): number {
        return tasks.filter(task => task.isCompleted).length;
    }

    getCompletionPercent(tasks: OnboardingTask[] = []): number {
        if (!tasks.length) return 0;
        return Math.round((this.getCompletedCount(tasks) / tasks.length) * 100);
    }
}
